import React, { useContext, useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { createStackNavigator } from "@react-navigation/stack";
import { keyBy } from "lodash";
import { AppTheme } from "../../utils/theme";
import { StrategyCategoryOrderContext } from "../../utils/strategy";
import { useExternalState } from "../../utils/me";
import { FurnitureContext } from "../../utils/furniture";
import { getResourceOrder } from "../../utils/contentful";
import { useNearbyResources } from "../../utils/resources";
import { cardStyleInterpolatorSlide } from "../../utils/navigation";
import DrawerButton from "../../components/DrawerButton";
import StrategyListItem from "../../components/StrategyListItem";
import CategoryListItem from "../../components/CategoryListItem";
import Strategy from "../../screens/Strategy";
import ResourceCategory from "../../screens/ResourceCategory";
import {
	StackContainer,
	Container,
	Background,
	Scroll,
	Title,
	CategoryList,
	BookmarkContainer,
} from "./styles";

const Stack = createStackNavigator();

const Navigator = () => {
	const {
		backgroundPattern,
		color: { backgroundAccent },
	} = useTheme() as AppTheme;

	return (
		<StackContainer>
			<Background source={backgroundPattern} />
			<DrawerButton />
			<Stack.Navigator
				headerMode="none"
				initialRouteName="Bookmarks"
				screenOptions={{
					cardStyle: { backgroundColor: "transparent" },
					// @ts-ignore
					cardStyleInterpolator: cardStyleInterpolatorSlide,
				}}>
				<Stack.Screen name="Bookmarks" component={Bookmarks} />
				<Stack.Screen
					name="ResourceCategory"
					component={ResourceCategory}
				/>
				<Stack.Screen
					name="ResourceCategoryNearMe"
					component={ResourceCategory}
					options={{
						cardStyle: {
							backgroundColor: backgroundAccent,
						},
					}}
				/>
				<Stack.Screen name="Strategy" component={Strategy} />
			</Stack.Navigator>
		</StackContainer>
	);
};

const Bookmarks = ({ navigation }) => {
	const [bookmarkedStrategies, setBookmarkedStrategies] = useState([]);
	const [bookmarkedResources, setBookmarkedResources] = useState([]);
	const [bookmarkedNearbyResources, setBookmarkedNearbyResources] = useState(
		[]
	);
	const [contentStatesMap, setContentsStateMap] = useState(null);
	const [resourceCategoryOrder, setResourceCategoryOrder] = useState(null);
	const [, setResourceOrder] = useState(null);
	const { available } = useContext(StrategyCategoryOrderContext);
	const { bookmarkStates, contentStates, logicStates } = useExternalState();
	const {
		bookmarksTitle,
		resourceCategoryIndex,
		myResourcesTitle,
		myStrategiesTitle,
		strings: { bookmarksEmpty },
	} = useContext(FurnitureContext);
	const [
		nearbyResourcesTitle,
		,
		nearbyResources,
		,
		,
		isNearbyResourcesEnabled,
	] = useNearbyResources();

	const strategies = available.map((category) => category.strategies).flat();

	useEffect(() => {
		if (logicStates && contentStates) {
			// if a content id appears in the content state map, it has been access or is Not New
			const _contentStatesMap = keyBy(contentStates, "contentId");
			setContentsStateMap(_contentStatesMap);

			const resourceIndex = resourceCategoryIndex.reduce(
				(acc, resourceCategory) =>
					acc.concat(
						resourceCategory.resources.map((strategy) => ({
							...strategy,
							_meta: {
								resourceCategoryId: resourceCategory._id,
								isNew: !_contentStatesMap[strategy._id],
							},
						}))
					),
				[]
			);
			const _resourceOrder = getResourceOrder(logicStates, resourceIndex);
			const availableResourceCategories = resourceCategoryIndex.reduce(
				(acc, resourceCategory) => {
					const matches = resourceCategory.resources
						.filter((resource) =>
							_resourceOrder.available.find(
								(_resource) => _resource._id === resource._id
							)
						)
						.map((resource) => {
							return {
								...resource,
								_meta: {
									resourceCategoryId: resource._id,
									isNew: !_contentStatesMap[resource._id],
								},
							};
						});

					if (matches.length > 0) {
						const hasNew = !!matches.find((m) => m?._meta?.isNew);
						const _resourceCategory = {
							...resourceCategory,
							_meta: {
								hasNew,
							},
							resources: matches,
						};

						acc.push(_resourceCategory);
					}

					return acc;
				},
				[]
			);

			const _resourceCategoryOrder = {
				all: resourceCategoryIndex,
				available: availableResourceCategories,
			};

			setResourceOrder(_resourceOrder);
			setResourceCategoryOrder(_resourceCategoryOrder);
		}
	}, [contentStates, logicStates]);

	useEffect(() => {
		if (bookmarkStates) {
			const bookmarkedContent = bookmarkStates.map((bookmark) => {
				return bookmark._id;
			});
			const _bookmarkedStrategies = strategies?.filter((strategy) => {
				return bookmarkedContent.includes(strategy._id);
			});
			const _bookmarkedResources =
				resourceCategoryOrder?.available.filter((resourceCategory) => {
					return bookmarkedContent.includes(resourceCategory._id);
				});
			const _bookmarkedNearbyResources = nearbyResources?.filter(
				(resouceCategory) =>
					bookmarkedContent.includes(resouceCategory._id)
			);
			setBookmarkedStrategies(_bookmarkedStrategies);
			setBookmarkedResources(_bookmarkedResources);
			setBookmarkedNearbyResources(_bookmarkedNearbyResources);
		}
	}, [bookmarkStates]);

	const isEmpty =
		!bookmarkedStrategies?.length &&
		!bookmarkedResources?.length &&
		!bookmarkedNearbyResources?.length;

	const renderEmpty = () => <Title type="h2">{bookmarksEmpty}</Title>;

	const renderStrategies = () => {
		if (!bookmarkedStrategies?.length) return null;
		return (
			<BookmarkContainer>
				<Title type="h2">{myStrategiesTitle}</Title>
				<CategoryList>
					{bookmarkedStrategies?.map((strategy) => (
						<StrategyListItem
							key={strategy._id}
							strategy={strategy}
							isNew={false}
							onPress={() => {
								navigation.navigate("Strategy", {
									strategyId: strategy._id,
								});
							}}
						/>
					))}
				</CategoryList>
			</BookmarkContainer>
		);
	};

	const renderResources = () => {
		if (!bookmarkedResources?.length) return null;
		return (
			<BookmarkContainer>
				<Title type="h2">{myResourcesTitle}</Title>
				<CategoryList>
					{bookmarkedResources?.map((resourceCategory) => {
						return (
							<CategoryListItem
								key={resourceCategory._id}
								item={resourceCategory}
								onPress={() => {
									navigation.navigate("ResourceCategory", {
										resourceCategoryId:
											resourceCategory._id,
										data: resourceCategory,
									});
								}}
								count={resourceCategory.resources.length}
								singular="resource"
								plural="resources"
								icon={resourceCategory.icon}
								payload={{ resourceCategory }}
								hasNew={
									!contentStatesMap?.[resourceCategory._id]
								}
							/>
						);
					})}
				</CategoryList>
			</BookmarkContainer>
		);
	};

	const renderNearbyResources = () => {
		if (!isNearbyResourcesEnabled()) return null;
		if (!bookmarkedNearbyResources?.length) return null;
		return (
			<BookmarkContainer>
				<Title type="h2">{nearbyResourcesTitle}</Title>
				<CategoryList>
					{bookmarkedNearbyResources?.map((resourceCategory) => {
						return (
							<CategoryListItem
								key={resourceCategory._id}
								item={resourceCategory}
								onPress={() => {
									navigation.navigate("ResourceCategory", {
										resourceCategoryId:
											resourceCategory._id,
										data: resourceCategory,
									});
								}}
								count={resourceCategory.resources.length}
								singular="resource"
								plural="resources"
								icon={resourceCategory.icon}
								payload={{ resourceCategory }}
								hasNew={
									!contentStatesMap?.[resourceCategory._id]
								}
							/>
						);
					})}
				</CategoryList>
			</BookmarkContainer>
		);
	};

	return (
		<Container>
			<Scroll scrollIndicatorInsets={{ right: 1 }}>
				<Title type="h3">{bookmarksTitle}</Title>
				{isEmpty && renderEmpty()}
				{renderStrategies()}
				{renderResources()}
				{renderNearbyResources()}
			</Scroll>
		</Container>
	);
};

export default Navigator;
